import { createContext, useContext, useCallback, useEffect, useRef, type ReactNode } from 'react';
import { type Locale, translate, setLocaleInStorage, getLocaleFromStorage } from './index';
import { useAuth } from '../lib/auth-context';
import { updateLocale } from '../lib/auth-client';

interface LocaleContextValue {
  locale: Locale;
  /** Persists the user's language preference (used by the switcher). Does NOT
   *  change the rendered locale — that is URL-derived and owned by <App>. */
  setLocale: (locale: Locale) => void;
  t: (key: string, params?: Record<string, string | number>) => string;
}

const LocaleContext = createContext<LocaleContextValue | null>(null);

interface LocaleProviderProps {
  children: ReactNode;
  /** The active locale, derived from the URL by the caller (URL is authoritative). */
  locale: Locale;
}

export function LocaleProvider({ children, locale }: LocaleProviderProps) {
  const setLocale = useCallback((next: Locale) => setLocaleInStorage(next), []);
  const t = useCallback(
    (key: string, params?: Record<string, string | number>) => translate(locale, key, params),
    [locale],
  );
  // Keep <html lang> in sync with the route locale on the client. Prerendered
  // pages already ship the correct lang, but the @spa fallback shell ships the
  // static default (ru) — on a /uz/* fallback route this corrects it to `uz`
  // once hydrated, so a crawler/user never sees a lang/content mismatch on a
  // non-prerendered page. No-op during SSR (useEffect doesn't run there).
  useEffect(() => {
    document.documentElement.lang = locale;
  }, [locale]);
  return (
    <LocaleContext.Provider value={{ locale, setLocale, t }}>{children}</LocaleContext.Provider>
  );
}

export function useLocale(): LocaleContextValue {
  const ctx = useContext(LocaleContext);
  if (!ctx) throw new Error('useLocale must be used within LocaleProvider');
  return ctx;
}

/**
 * The active locale, or `fallback` when no provider is mounted. For leaf
 * components that are also rendered bare (widget hosts, tests) and only need
 * the language as a hint — never for anything that translates copy, where a
 * missing provider is a bug `useLocale` should throw on.
 */
export function useLocaleOr(fallback: Locale): Locale {
  return useContext(LocaleContext)?.locale ?? fallback;
}

/**
 * Pushes the user's EXPLICIT locale preference (localStorage, written only by
 * the language switcher) up to the server whenever it changes (and once right
 * after login), so locale-aware channels (saved-search Telegram/email alerts)
 * address them in the language they chose. Renders nothing.
 *
 * Deliberately does NOT sync the URL-derived render `locale` — landing on a
 * URL the user never chose (a Telegram/email alert deep-link, or the
 * Google-OAuth return to the bare FRONTEND_URL) must not flip their saved
 * alert language. `locale` is only read here to re-trigger evaluation after a
 * switcher-driven navigation (the switcher writes storage, then navigates).
 *
 * Must render INSIDE both LocaleProvider and AuthProvider. It never fires for
 * guests or during SSR (no auth), and dedups via a ref so there's no loop and no
 * redundant PATCH when the server already agrees.
 */
export function LocaleServerSync(): null {
  // `locale` is read ONLY to re-trigger the effect after a switcher-driven
  // navigation (the switcher writes storage, then navigates). The value synced
  // to the server is the stored PREFERENCE, never the URL-derived render locale.
  const { locale } = useLocale();
  const { status, user } = useAuth();
  const syncedRef = useRef<Locale | null>(null);

  // Forget the synced marker on logout so the next user re-evaluates cleanly.
  useEffect(() => {
    if (status !== 'authed') syncedRef.current = null;
  }, [status]);

  useEffect(() => {
    if (status !== 'authed' || !user) return;
    // The user's explicit choice — null means they never used the switcher, so
    // there is nothing to sync and the server locale must be left alone (a
    // landing URL must never flip their saved alert language).
    const pref = getLocaleFromStorage();
    if (pref === null) return;
    // Already pushed this exact preference up this session — nothing to do.
    if (syncedRef.current === pref) return;
    // First reconciliation after login: if the server already matches the
    // stored preference, record it and skip the network call. Only a real
    // change (or a server/preference mismatch) triggers the PATCH.
    if (syncedRef.current === null && user.locale === pref) {
      syncedRef.current = pref;
      return;
    }
    syncedRef.current = pref;
    void updateLocale(pref).catch(() => {
      // Best-effort — clear the marker so a later change retries.
      syncedRef.current = null;
    });
  }, [status, user, locale]);

  return null;
}

/** Convenience alias — returns just the t() function. */
export function useT() {
  return useLocale().t;
}
